import React from 'react';
import { DollarSign, Users, Gift, TrendingUp, CheckCircle, ShieldCheck, Award, Zap, Star, Crown } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const MemberCard = ({ member }) => {
    const [imgError, setImgError] = React.useState(false);
    const initials = member.name ? member.name.split(' ').map(n => n[0]).join('').slice(0, 2) : '??';

    return (
        <div className="group cursor-default">
            <div className="bg-white border border-slate-200 p-8 text-center rounded-[2.5rem] transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-2 bg-indigo-900 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="w-32 h-32 mx-auto mb-8 relative">
                    <div className="absolute inset-0 bg-slate-100 rounded-[2rem] transform rotate-6 group-hover:rotate-12 transition-transform duration-500"></div>
                    <div className="absolute inset-0 bg-white border-2 border-slate-200 rounded-[2rem] overflow-hidden shadow-lg transform -rotate-3 group-hover:rotate-0 transition-transform duration-500">
                        {member.image && !imgError ? (
                            <img
                                src={member.image}
                                alt={member.name}
                                className="w-full h-full object-cover filter grayscale-0 group-hover:scale-110 transition-transform duration-700"
                                onError={() => setImgError(true)}
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-4xl font-black text-slate-200 uppercase tracking-tighter bg-slate-50">
                                {initials}
                            </div>
                        )}
                    </div>
                </div>
                <h3 className="font-black text-2xl text-slate-900 uppercase mb-1 tracking-tighter">{member.name || "Customer Name"}</h3>
                <p className="text-primary-600 font-black text-[10px] uppercase tracking-[0.2em] mb-6">{member.role || "Executive Position"}</p>
                <p className="text-slate-500 text-sm leading-relaxed font-medium italic px-4">"{member.bio || "Leading the industry with passion and integrity..."}"</p>
            </div>
        </div>
    );
};

const IncomePlan = () => {
    const { siteContent, kits, products, settings } = useApp();
    const [kitRate, setKitRate] = React.useState(siteContent.incomePlan?.kitRate || 500);

    // Update local state when siteContent loads/changes
    React.useEffect(() => {
        if (siteContent.incomePlan?.kitRate) {
            setKitRate(siteContent.incomePlan.kitRate);
        }
    }, [siteContent.incomePlan?.kitRate]);

    // Dynamically calculate level data based on settings or default
    // Dynamically calculate level data based on settings or default
    const levelData = (settings.commissionLevels || []).map((rate, index) => {
        const level = index + 1;
        const size = Math.pow(5, level);
        const currentKitRate = Number(kitRate) || 0;
        const totalSale = size * currentKitRate;
        const income = (totalSale * rate) / 100;

        return {
            level,
            size,
            rate: currentKitRate,
            sale: totalSale,
            per: `${rate}%`,
            income: income
        };
    });

    const totalIncome = levelData.reduce((acc, curr) => acc + curr.income, 0);

    // Self Purchase (Level 0) Data
    const selfRate = settings.selfPurchaseBonus || 0;
    const selfIncome = (Number(kitRate) || 0) * (selfRate / 100);
    const selfRow = {
        level: 'Self (0)',
        size: 1,
        rate: Number(kitRate) || 0,
        sale: Number(kitRate) || 0,
        per: `${selfRate}%`,
        income: selfIncome
    };

    // Grand Total including Self
    const grandTotal = totalIncome + selfIncome;

    const kitFeatures = [
        { name: 'Active Business ID', starter: true, premium: true, business: true },
        { name: `${settings?.commissionLevels?.length || 7}-Level Commission`, starter: true, premium: true, business: true },
        { name: 'Training Support', starter: 'Basic', premium: 'Priority', business: 'Mastery' },
        { name: 'Direct Sponsoring', starter: 'Unlimited', premium: 'Unlimited', business: 'Unlimited' },
        { name: 'Gift Coupons', starter: false, premium: true, business: true },
        { name: 'Rank Rewards', starter: false, premium: true, business: true },
    ];

    return (
        <div className="pt-20 pb-16 bg-slate-50 min-h-screen font-jakarta tracking-tight text-slate-900">
            <style dangerouslySetInnerHTML={{
                __html: `
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=Outfit:wght@500;800;900&family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&display=swap');
                .font-jakarta { font-family: 'Plus Jakarta Sans', sans-serif; }
                .font-outfit { font-family: 'Outfit', sans-serif; }
                .font-playfair { font-family: 'Playfair Display', serif; }
            `}} />
            {/* Elegant Compact Hero Section */}
            <div className="relative bg-slate-950 overflow-hidden py-12 px-4 shadow-xl border-b border-indigo-900/30">
                {/* Complex Gradient Background */}
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-900/60 via-slate-950 to-slate-950"></div>

                <div className="max-w-7xl mx-auto relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="text-left">
                        <h1 className="text-4xl md:text-5xl font-playfair font-black text-white italic tracking-tight leading-tight">
                            MLM <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-indigo-400">Business Plan</span>
                        </h1>
                        <p className="text-slate-400 text-sm font-medium tracking-wide mt-2">
                            Mathematically optimized for <span className="text-white">Exponential Growth.</span>
                        </p>
                    </div>

                    <div className="hidden md:block h-12 w-[1px] bg-indigo-900"></div>

                    <div className="flex gap-8 text-xs font-bold text-slate-500 uppercase tracking-widest">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                            Live System
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                            {settings?.commissionLevels?.length || 7}-Level Payout
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 space-y-24">

                {/* 1. MLM Tree Structure */}
                <section className="relative">
                    {/* Background Decorative Accents */}
                    <div className="absolute -top-24 -left-20 w-96 h-96 bg-indigo-500/10 rounded-full blur-[120px] pointer-events-none"></div>
                    <div className="absolute top-1/2 -right-20 w-80 h-80 bg-emerald-500/10 rounded-full blur-[100px] pointer-events-none"></div>

                    <div className="grid lg:grid-cols-2 gap-20 items-center relative z-10">
                        <div className="space-y-12">
                            <div className="inline-flex items-center gap-3 px-10 py-4 bg-white text-indigo-600 rounded-3xl text-sm font-black uppercase tracking-[0.3em] leading-none border-2 border-indigo-100 shadow-[0_15px_40px_rgba(79,70,229,0.1)]">
                                <div className="w-2.5 h-2.5 bg-indigo-600 rounded-full animate-ping"></div>
                                5-Leg Structure
                            </div>

                            <div className="space-y-6">
                                <h2 className="text-6xl md:text-8xl font-black text-slate-950 leading-[0.9] tracking-tighter uppercase relative">
                                    Build Your <br />
                                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-800 italic pr-4">Empire.</span>
                                </h2>
                                <div className="h-2 w-32 bg-gradient-to-r from-indigo-600 to-transparent rounded-full"></div>
                                <p className="text-2xl md:text-3xl text-slate-500 leading-relaxed font-medium max-w-xl">
                                    Our system uses a <span className="text-indigo-600 font-extrabold border-b-4 border-indigo-500/20 italic bg-indigo-50/50 px-2 rounded-lg">5-Leg Duplication model</span>. By bringing in 5 members, you create a solid foundation for exponential income across {settings?.commissionLevels?.length || 7} levels.
                                </p>
                            </div>

                            <div className="grid gap-8">
                                {[
                                    { icon: Users, title: "5 Direct Legs", desc: "Build a solid foundation with five primary members.", color: "from-indigo-600 to-indigo-800", shadow: "shadow-indigo-200" },
                                    { icon: Zap, title: "Exponential Growth", desc: "Duplicate the system to unlock massive team potential.", color: "from-emerald-500 to-emerald-700", shadow: "shadow-emerald-200" },
                                    { icon: ShieldCheck, title: "Maximum Stability", desc: "A balanced structure ensures long-term business health.", color: "from-amber-500 to-amber-700", shadow: "shadow-amber-200" }
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center gap-8 p-10 bg-white border border-slate-100 rounded-[3.5rem] hover:shadow-[0_30px_60px_rgba(0,0,0,0.08)] transition-all duration-500 group cursor-default">
                                        <div className={`w-20 h-20 bg-gradient-to-br ${item.color} rounded-[2rem] flex items-center justify-center shrink-0 shadow-2xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 ${item.shadow}`}>
                                            <item.icon className="w-10 h-10 text-white" />
                                        </div>
                                        <div>
                                            <h4 className="font-black text-3xl text-slate-950 mb-1.5 tracking-tight group-hover:text-indigo-600 transition-colors">{item.title}</h4>
                                            <p className="text-slate-500 text-lg font-medium leading-normal">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="relative group">
                            <div className="absolute inset-0 bg-indigo-600/5 rounded-[4rem] blur-3xl group-hover:bg-indigo-600/10 transition-colors"></div>
                            <div className="relative bg-white p-12 md:p-16 rounded-[4rem] border border-slate-200 shadow-2xl overflow-hidden">
                                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-emerald-400 via-indigo-500 to-purple-500 opacity-50"></div>
                                <div className="flex flex-col items-center gap-16">
                                    {/* Root */}
                                    <div className="w-24 h-24 bg-gradient-to-br from-indigo-900 to-slate-900 rounded-[2rem] flex items-center justify-center text-white shadow-xl relative z-10 border-4 border-indigo-100/20">
                                        <Crown className="w-10 h-10" />
                                        <div className="absolute -bottom-16 left-1/2 w-1 h-16 bg-slate-200 -translate-x-1/2"></div>
                                    </div>

                                    {/* Level 1 Connections */}
                                    <div className="relative w-full">
                                        <div className="absolute top-0 left-0 right-0 h-1 bg-slate-100 mx-auto w-[85%] rounded-full shadow-inner"></div>
                                        <div className="grid grid-cols-5 gap-4 mt-1">
                                            {[
                                                { label: 'Leg 1', color: 'bg-emerald-600', text: 'text-emerald-600', shadow: 'shadow-emerald-200' },
                                                { label: 'Leg 2', color: 'bg-rose-600', text: 'text-rose-600', shadow: 'shadow-rose-200' },
                                                { icon: Users, label: 'Leg 3', color: 'bg-amber-600', text: 'text-amber-600', shadow: 'shadow-amber-200' },
                                                { icon: Users, label: 'Leg 4', color: 'bg-sky-600', text: 'text-sky-600', shadow: 'shadow-sky-200' },
                                                { icon: Users, label: 'Leg 5', color: 'bg-purple-600', text: 'text-purple-600', shadow: 'shadow-purple-200' }
                                            ].map((leg, i) => (
                                                <div key={i} className="flex flex-col items-center gap-6 group/leg">
                                                    <div className="w-1 h-8 bg-slate-100 rounded-full"></div>
                                                    <div className={`w-14 h-14 ${leg.color} rounded-2xl flex items-center justify-center text-white shadow-lg transition-all duration-300 transform group-hover/leg:scale-125 group-hover/leg:rotate-6 ${leg.shadow} hover:shadow-2xl`}>
                                                        <Users className="w-7 h-7" />
                                                    </div>
                                                    <p className={`text-[10px] font-black uppercase tracking-widest ${leg.text}`}>{leg.label}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Level 2 Hint */}
                                    <div className="pt-8 text-center space-y-6">
                                        <div className="flex justify-center -space-x-4">
                                            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => {
                                                const dots = ['bg-emerald-500', 'bg-rose-500', 'bg-amber-500', 'bg-sky-500', 'bg-purple-500'];
                                                return (
                                                    <div key={i} className="w-10 h-10 rounded-full bg-white border-2 border-slate-50 shadow-sm flex items-center justify-center shrink-0 transform hover:-translate-y-2 transition-transform">
                                                        <div className={`w-3 h-3 rounded-full ${dots[i % dots.length]}`}></div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <p className="text-sm font-black text-slate-400 uppercase tracking-[0.3em] bg-slate-50 py-2 px-6 rounded-full inline-block">Expanding to 25 Members...</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 2. Joining Kits Section */}
                <section>
                    <div className="text-center mb-16">
                        <div className="h-2 w-20 bg-indigo-600 mx-auto mb-6 rounded-full"></div>
                        <h2 className="text-4xl md:text-5xl font-black text-slate-900 uppercase tracking-tighter mb-4">Choose Your <span className="text-indigo-600">Joining Kit</span></h2>
                        <p className="text-slate-500 font-medium max-w-lg mx-auto">Select the perfect start for your journey with Clean India.</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-12">
                        {kits.map((kit) => (
                            <div key={kit.id} className="group relative">
                                <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-emerald-500 rounded-[2.5rem] opacity-20 group-hover:opacity-40 transition duration-500 blur-lg"></div>
                                <div className={`h-full bg-white relative rounded-[2.5rem] transition-all duration-500 hover:-translate-y-2 z-10 border border-slate-100 overflow-hidden flex flex-col shadow-sm hover:shadow-2xl`}>
                                    {/* Large Kit Image Container */}
                                    <div className="relative h-64 w-full bg-slate-50 overflow-hidden border-b border-slate-100">
                                        <div className="absolute inset-0 bg-gradient-to-tr from-indigo-500/5 to-transparent z-0"></div>
                                        <img
                                            src={kit.image || "https://images.unsplash.com/photo-1563453392212-326f5e854473?auto=format&fit=crop&w=400&q=80"}
                                            alt={kit.name}
                                            className="w-full h-full object-contain p-4 mix-blend-multiply group-hover:scale-110 transition-transform duration-700 relative z-10"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = "https://images.unsplash.com/photo-1563453392212-326f5e854473?auto=format&fit=crop&w=400&q=80";
                                            }}
                                        />
                                        {/* Status Badge */}
                                        <div className="absolute top-4 right-4 z-20">
                                            <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-white shadow-lg border border-slate-100 ${kit.id === 'premium' ? 'text-amber-600' : 'text-indigo-600'}`}>
                                                {String(kit.id || '').toUpperCase()} PLAN
                                            </span>
                                        </div>
                                    </div>

                                    <div className="p-8 flex-1 flex flex-col">
                                        <div className="flex justify-between items-end mb-6">
                                            <div>
                                                <h3 className={`text-2xl font-black uppercase tracking-tighter ${kit.color} group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-indigo-600 group-hover:to-purple-600 transition-colors`}>
                                                    {kit.name}
                                                </h3>
                                                <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] mt-1">One Time Investment</p>
                                            </div>
                                            <div className="text-right">
                                                <div>
                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Starting From</p>
                                                    <h4 className="text-3xl font-black text-slate-900 group-hover:text-amber-600 transition-colors">₹{(kit.price || 0).toLocaleString()}</h4>
                                                </div>
                                                <div className="flex justify-end gap-1.5 mt-1">
                                                    {kit.gst > 0 && <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-1.5 rounded opacity-70">GST: {kit.gst}%</span>}
                                                    {kit.discount > 0 && <span className="text-[9px] font-bold text-indigo-600 bg-indigo-50 px-1.5 rounded">Disc: {kit.discount}%</span>}
                                                </div>
                                            </div>
                                        </div>

                                        <p className="text-slate-500 text-sm leading-relaxed mb-8 font-medium line-clamp-2">{kit.description}</p>

                                        {/* Real Package Contents */}
                                        <div className="mb-8 p-4 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Package Contents</p>
                                            <div className="flex flex-wrap gap-1.5">
                                                {kit.contents && kit.contents.length > 0 ? kit.contents.map((item, idx) => {
                                                    const prod = products.find(p => p.id === item.productId);
                                                    return (
                                                        <div key={idx} className="flex items-center gap-1.5 bg-white border border-slate-200 px-2 py-1 rounded-lg">
                                                            <span className="text-[10px] font-bold text-slate-700">{prod?.name || 'Item'}</span>
                                                            <span className="text-[9px] font-black text-indigo-600 bg-indigo-50 px-1.5 rounded-md">x{item.qty}</span>
                                                        </div>
                                                    );
                                                }) : (
                                                    <div className="text-xs font-bold text-slate-400 italic">Full Business Kit Items</div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="space-y-3 mb-8">
                                            {kitFeatures.map((feature, i) => (
                                                <div key={i} className="flex items-center justify-between py-1.5">
                                                    <span className="text-slate-500 text-[11px] font-bold flex items-center gap-2">
                                                        <CheckCircle className={`w-3.5 h-3.5 ${typeof feature[kit.id] === 'boolean' && !feature[kit.id] ? 'text-slate-200' : 'text-emerald-500'}`} />
                                                        {feature.name}
                                                    </span>
                                                    {typeof feature[kit.id] !== 'boolean' && (
                                                        <span className="text-indigo-600 text-[10px] font-black uppercase tracking-wide">{feature[kit.id]}</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>

                                        <button className="w-full mt-auto py-4 rounded-2xl bg-slate-900 text-white font-black uppercase tracking-widest text-sm hover:bg-indigo-600 hover:shadow-lg hover:shadow-indigo-500/30 transition-all duration-300">
                                            Choose Plan
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* 3. Income Projection Section */}
                <section className="relative">
                    <div className="absolute inset-0 bg-indigo-50/50 -skew-y-3 transform origin-top-left rounded-[3rem] -z-10"></div>

                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-4 font-outfit uppercase tracking-tight">
                            {settings?.commissionLevels?.length || 7} Level <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-indigo-600">Income Potential</span>
                        </h2>
                        <p className="text-lg text-slate-500 font-medium max-w-2xl mx-auto font-outfit mb-8">
                            See your potential monthly earnings based on your team size.
                        </p>

                        <div className="max-w-md mx-auto bg-indigo-50 p-4 rounded-2xl border border-indigo-100 flex items-center justify-center gap-4 shadow-sm mb-8">
                            <label className="text-sm font-bold text-indigo-900 uppercase tracking-wider">
                                Current Kit Rate (BV):
                            </label>
                            <div className="relative">
                                <DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-indigo-500" />
                                <input
                                    type="number"
                                    value={kitRate}
                                    onChange={(e) => setKitRate(e.target.value)}
                                    className="pl-9 pr-4 py-2 w-32 rounded-lg border border-indigo-200 font-bold text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-center"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl bg-white border border-slate-200">
                        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-emerald-400 via-indigo-500 to-purple-500"></div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50 border-b border-slate-200">
                                        <th className="py-6 px-8 text-xs font-black text-slate-400 uppercase tracking-[0.2em] font-outfit text-center">Level</th>
                                        <th className="py-6 px-8 text-xs font-black text-slate-400 uppercase tracking-[0.2em] font-outfit">Team Size</th>
                                        <th className="py-6 px-8 text-xs font-black text-slate-400 uppercase tracking-[0.2em] font-outfit">Kit Rate</th>
                                        <th className="py-6 px-8 text-xs font-black text-slate-400 uppercase tracking-[0.2em] font-outfit">Total Sale</th>
                                        <th className="py-6 px-8 text-xs font-black text-slate-400 uppercase tracking-[0.2em] font-outfit">Commission</th>
                                        <th className="py-6 px-8 text-xs font-black text-slate-400 uppercase tracking-[0.2em] font-outfit text-right">Income</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Level 0 (Self) Row */}
                                    <tr className="bg-indigo-50/30 hover:bg-indigo-50 transition-colors duration-300 border-b border-indigo-100">
                                        <td className="py-6 px-8 text-center">
                                            <div className="w-10 h-10 mx-auto rounded-full bg-emerald-500 text-white flex items-center justify-center font-outfit font-bold text-lg shadow-md">
                                                0
                                            </div>
                                        </td>
                                        <td className="py-6 px-8">
                                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm">
                                                <Users className="w-4 h-4 text-emerald-600" />
                                                <span className="font-outfit font-black text-slate-900 text-base">You (1)</span>
                                            </div>
                                        </td>
                                        <td className="py-6 px-8">
                                            <span className="inline-flex items-center px-4 py-2 rounded-full bg-amber-50 text-amber-800 text-base font-bold font-outfit border border-amber-100 shadow-sm">
                                                ₹{selfRow.rate}
                                            </span>
                                        </td>
                                        <td className="py-6 px-8">
                                            <span className="inline-flex items-center px-4 py-2 rounded-full bg-purple-50 text-purple-900 text-base font-black font-outfit border border-purple-100 shadow-sm">
                                                ₹{selfRow.sale.toLocaleString()}
                                            </span>
                                        </td>
                                        <td className="py-6 px-8">
                                            <span className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-100 text-emerald-800 text-base font-black uppercase tracking-wide font-outfit border border-emerald-200 shadow-sm">
                                                {selfRow.per}
                                            </span>
                                        </td>
                                        <td className="py-6 px-8 text-right">
                                            <span className="inline-flex items-center px-5 py-2.5 rounded-full bg-slate-900 text-white text-xl font-black font-outfit shadow-md hover:shadow-lg transition-shadow border border-slate-700">
                                                ₹{selfRow.income.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                            </span>
                                        </td>
                                    </tr>
                                    {levelData.map((row, index) => (
                                        <tr key={row.level} className={`group transition-colors duration-300 hover:bg-indigo-50/50 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                                            <td className="py-6 px-8 text-center">
                                                <div className="w-10 h-10 mx-auto rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center font-outfit font-bold text-lg shadow-md group-hover:scale-110 transition-transform">
                                                    {row.level}
                                                </div>
                                            </td>
                                            <td className="py-6 px-8">
                                                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-50 border border-indigo-100 shadow-sm">
                                                    <Users className="w-4 h-4 text-indigo-600" />
                                                    <span className="font-outfit font-black text-indigo-900 text-base">{(row.size || 0).toLocaleString()}</span>
                                                </div>
                                            </td>
                                            <td className="py-6 px-8">
                                                <span className="inline-flex items-center px-4 py-2 rounded-full bg-amber-50 text-amber-800 text-base font-bold font-outfit border border-amber-100 shadow-sm">
                                                    ₹{row.rate}
                                                </span>
                                            </td>
                                            <td className="py-6 px-8">
                                                <span className="inline-flex items-center px-4 py-2 rounded-full bg-purple-50 text-purple-900 text-base font-black font-outfit border border-purple-100 shadow-sm">
                                                    ₹{(row.sale || 0).toLocaleString()}
                                                </span>
                                            </td>
                                            <td className="py-6 px-8">
                                                <span className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-100 text-emerald-800 text-base font-black uppercase tracking-wide font-outfit border border-emerald-200 shadow-sm">
                                                    {row.per}
                                                </span>
                                            </td>
                                            <td className="py-6 px-8 text-right">
                                                <span className="inline-flex items-center px-5 py-2.5 rounded-full bg-slate-900 text-white text-xl font-black font-outfit shadow-md hover:shadow-lg transition-shadow border border-slate-700">
                                                    ₹{(row.income || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}

                                    <tr className="bg-slate-900 text-white relative overflow-hidden">
                                        <td colSpan="4" className="py-10 px-8">
                                            <div className="relative z-10">
                                                <p className="font-outfit font-bold text-indigo-300 text-sm uppercase tracking-widest mb-1">Total Potential Breakdown</p>
                                                <p className="font-outfit text-3xl font-bold">Monthly Passive Income</p>
                                            </div>
                                            {/* Decorative sheen */}
                                            <div className="absolute top-0 right-0 h-full w-1/2 bg-gradient-to-l from-indigo-600/20 to-transparent"></div>
                                        </td>
                                        <td colSpan="2" className="py-10 px-8 text-right relative">
                                            <div className="relative z-10">
                                                <p className="font-outfit font-black text-5xl md:text-6xl text-white drop-shadow-xl">
                                                    ₹{(grandTotal || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>

                {/* 4. Leadership Section */}
                <section className="pb-20">
                    <div className="text-center mb-16">
                        <div className="h-2 w-20 bg-indigo-900 mx-auto mb-6 rounded-full"></div>
                        <h2 className="text-4xl md:text-5xl font-black text-slate-900 uppercase tracking-tighter mb-4">Board of Directors</h2>
                        <p className="text-slate-400 font-medium max-w-lg mx-auto">The executive leadership committed to institutional excellence and network integrity.</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-12">
                        {siteContent.about.team && siteContent.about.team.map((member) => (
                            <MemberCard key={member.id} member={member} />
                        ))}
                    </div>
                </section>
            </div >
        </div >
    );
};

export default IncomePlan;
